<template>
  <form class="local-login" @submit.prevent="submit" novalidate>
    <p v-if="notice && !error" class="local-login__notice" role="status">{{ notice }}</p>
    <label class="local-login__field">
      <span>Username</span>
      <input
        ref="userInput"
        v-model.trim="username"
        name="username"
        autocomplete="username"
        autocapitalize="none"
        spellcheck="false"
        :disabled="busy"
        required
      />
    </label>
    <label class="local-login__field">
      <span>Password</span>
      <input
        v-model="password"
        name="password"
        type="password"
        autocomplete="current-password"
        :disabled="busy"
        required
      />
    </label>
    <p v-if="error" class="local-login__error" role="alert">{{ error }}</p>
    <button type="submit" class="local-login__submit" :disabled="busy || !username || !password">
      {{ busy ? 'Signing in…' : 'Sign in' }}
    </button>
  </form>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { loginLocal, type LocalUser } from '@/utils/localSession';

defineProps<{ notice?: string | null }>();
const emit = defineEmits<{ (e: 'signed-in', user: LocalUser): void }>();

const username = ref('');
const password = ref('');
const error = ref<string | null>(null);
const busy = ref(false);
const userInput = ref<HTMLInputElement | null>(null);

onMounted(() => userInput.value?.focus());

async function submit() {
  if (busy.value || !username.value || !password.value) return;
  busy.value = true;
  error.value = null;
  const r = await loginLocal(username.value, password.value);
  busy.value = false;
  password.value = '';
  if ('error' in r) error.value = r.error;
  else emit('signed-in', r.user);
}
</script>

<style scoped>
.local-login {
  display: flex;
  flex-direction: column;
  gap: 14px;
  width: 100%;
  text-align: left;
}
.local-login__field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 14px;
  color: #48545f;
}
.local-login__field input {
  padding: 10px 12px;
  border: 1px solid #d8dee5;
  border-radius: 5px;
  font-size: 16px;
  color: #1a222c;
  background: #ffffff;
}
.local-login__field input:focus {
  outline: 2px solid #2b6d94;
  outline-offset: 1px;
  border-color: #2b6d94;
}
.local-login__error {
  color: #b83a2f;
  background: #f7e1de;
  border-radius: 5px;
  padding: 8px 10px;
  font-size: 14px;
}
.local-login__notice {
  color: #48545f;
  background: #f6f8fa;
  border-radius: 5px;
  padding: 8px 10px;
  font-size: 14px;
}
.local-login__submit {
  background-color: #5E6977;
  color: white;
  border: none;
  padding: 12px 20px;
  border-radius: 5px;
  font-size: 16px;
  font-weight: bold;
  cursor: pointer;
  transition: background-color 0.3s ease;
  width: 100%;
}
.local-login__submit:hover:not(:disabled) { background-color: #4A5460; }
.local-login__submit:disabled { opacity: 0.6; cursor: default; }
</style>
